import React, { useEffect } from 'react';
import OnboardingLayout from './OnboardingLayout';
import { ONBOARDING_CONFIG, BYPASS_ONBOARDING_AUTH } from './OnboardingConfig';
import * as Screens from './screens/OnboardingScreens';
import { useOnboarding } from '../../context/OnboardingContext';
import { supabase } from '../../supabaseClient';

const OnboardingContainer = ({ theme = 'light', user, handleLogout }) => {
  const { index, setIndex, completeOnboarding } = useOnboarding();

  const currentScreenConfig = ONBOARDING_CONFIG[index];
  const progress = (index + 1) / ONBOARDING_CONFIG.length;
  const isAuthenticated = !!user?.id;
  const shouldSkipAuth = isAuthenticated || BYPASS_ONBOARDING_AUTH;

  // AUTO-SKIP: If the current screen is 'auth' and we should skip it
  useEffect(() => {
    if (currentScreenConfig.id === 'auth' && shouldSkipAuth) {
      setIndex(index + 1);
    }
  }, [index, shouldSkipAuth, currentScreenConfig.id, setIndex]);

  // GUARD: Payment screen is only accessible when logged in
  useEffect(() => {
    const LAST_STEP_INDEX = ONBOARDING_CONFIG.length - 1;
    const isPaymentStep = index === LAST_STEP_INDEX;

    if (isPaymentStep && !isAuthenticated) {
      console.warn("[GUARD] Payment step reached without authentication. Redirecting to auth screen.");
      // Find the index of the auth screen dynamically if possible, or use hardcoded 4
      const authIndex = ONBOARDING_CONFIG.findIndex(s => s.id === 'auth');
      if (authIndex !== -1) {
        setIndex(authIndex);
      }
    }
  }, [index, isAuthenticated, setIndex]);

  const handleGoogleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
    if (error) alert(error.message);
  };

  const handleNext = () => {
    // Check if next screen is auth and we should skip it
    let nextIndex = index + 1;
    if (nextIndex < ONBOARDING_CONFIG.length && ONBOARDING_CONFIG[nextIndex].id === 'auth' && shouldSkipAuth) {
      nextIndex += 1;
    }

    const SUMMARY_INDEX = 7;

    if (index === SUMMARY_INDEX) {
      completeOnboarding();
    } else if (nextIndex < ONBOARDING_CONFIG.length) {
      setIndex(nextIndex);
    } else {
      completeOnboarding();
    }
  };

  const handleBack = () => {
    let prevIndex = index - 1;
    // Skip auth screen on back if we should skip it
    if (prevIndex >= 0 && ONBOARDING_CONFIG[prevIndex].id === 'auth' && shouldSkipAuth) {
      prevIndex -= 1;
    }

    if (prevIndex >= 0) {
      setIndex(prevIndex);
    }
  };

  const renderScreen = () => {
    switch (currentScreenConfig.id) {
      case 'hero': return <Screens.HeroScreen onGoogleSignIn={handleGoogleSignIn} onNext={handleNext} user={user} />;
      case 'problem': return <Screens.ProblemScreen onNext={handleNext} onBack={handleBack} />;
      case 'game-based': return <Screens.GameBasedScreen onNext={handleNext} onBack={handleBack} />;
      case 'daily-reps': return <Screens.DailyRepsScreen onNext={handleNext} user={user} handleGoogleLogin={handleGoogleSignIn} />;
      case 'auth': return <Screens.GoogleSignInScreen onGoogleSignIn={handleGoogleSignIn} />;
      case 'goal': return <Screens.GoalSelectionScreen onNext={handleNext} />;
      case 'commitment': return <Screens.CommitmentScreen onNext={handleNext} onBack={handleBack} />;
      case 'summary': return <Screens.SummaryScreen onNext={handleNext} onBack={handleBack} />;
      default: return null;
    }
  };

  // Config-driven layout visibility (replaces hardcoded index checks)
  const showBack = index > 0 && !currentScreenConfig.hideLayoutBack;
  const showNext = !currentScreenConfig.hideLayoutCTA;

  return (
    <OnboardingLayout
      progress={progress}
      onNext={handleNext}
      onBack={handleBack}
      showBack={showBack}
      showNext={showNext}
      fullScreen={currentScreenConfig.fullScreen}
      nextLabel={currentScreenConfig.nextLabel || (index === ONBOARDING_CONFIG.length - 1 ? "Get Started" : "Continue")}
      theme={theme}
      user={user}
      handleLogout={handleLogout}
    >
      {renderScreen()}
    </OnboardingLayout>
  );
};

export default OnboardingContainer;
